import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, FileText, Video, Star, Trash2, UploadCloud, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { ROLE_LABELS } from "@/lib/constants";
import type { TutorialPdf, TutorialTopico, TutorialVideo } from "@shared/schema";

const PAPEIS_TUTORIAL = ["admin", "rt", "tv", "parceiro_negocios"] as const;

function formatBytes(bytes: number) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

function PdfManager({ papel, token }: { papel: string; token: string }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: pdfs, isLoading } = useQuery<TutorialPdf[]>({
    queryKey: ["/api/owner/tutoriais/pdfs", papel],
    queryFn: async () => {
      const res = await fetch(`/api/owner/tutoriais/pdfs?papel=${papel}`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error("Erro ao carregar PDFs");
      return res.json();
    },
    enabled: !!token,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0];
      if (!file) throw new Error("Selecione o arquivo .pdf");
      const params = new URLSearchParams({ papel, nomeArquivo: file.name });
      const res = await fetch(`/api/owner/tutoriais/pdfs?${params}`, {
        method: "POST",
        headers: { ...authHeaders(token), "Content-Type": "application/pdf" },
        body: file,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao enviar PDF");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/pdfs", papel] });
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast({ title: "PDF enviado com sucesso" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const setPrincipalMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/owner/tutoriais/pdfs/${id}/principal`, {
        method: "PUT",
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error("Erro ao definir como principal");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/pdfs", papel] });
      toast({ title: "PDF principal atualizado" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/owner/tutoriais/pdfs/${id}`, {
        method: "DELETE",
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error("Erro ao excluir PDF");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/pdfs", papel] });
      toast({ title: "PDF removido" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  return (
    <Card className="border-none shadow-md mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <FileText className="w-4 h-4" /> PDF completo — {ROLE_LABELS[papel]}
        </CardTitle>
        <CardDescription className="text-xs">
          Documento único cobrindo todos os tópicos deste papel
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor={`pdf-file-${papel}`} className="text-xs">Novo PDF</Label>
            <Input id={`pdf-file-${papel}`} type="file" accept=".pdf" ref={fileInputRef} />
          </div>
          <Button size="sm" onClick={() => uploadMutation.mutate()} disabled={uploadMutation.isPending}>
            {uploadMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
            )}
            Enviar
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-14 rounded-lg" />
        ) : !pdfs || pdfs.length === 0 ? (
          <p className="text-xs text-slate-500 py-2">Nenhum PDF enviado ainda para este papel.</p>
        ) : (
          pdfs.map((pdf) => (
            <div key={pdf.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-900">v{pdf.versao}</p>
                  {pdf.principal && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Principal</Badge>}
                </div>
                <p className="text-xs text-slate-500">{pdf.nomeArquivo} · {formatBytes(pdf.tamanhoBytes)}</p>
              </div>
              <div className="flex items-center gap-2">
                {!pdf.principal && (
                  <Button size="sm" variant="outline" onClick={() => setPrincipalMutation.mutate(pdf.id)} disabled={setPrincipalMutation.isPending}>
                    <Star className="w-3.5 h-3.5 mr-1.5" /> Tornar principal
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteMutation.mutate(pdf.id)} disabled={deleteMutation.isPending}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function TopicoVideos({ topico, token }: { topico: TutorialTopico; token: string }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: videos, isLoading } = useQuery<TutorialVideo[]>({
    queryKey: ["/api/owner/tutoriais/topicos", topico.id, "videos"],
    queryFn: async () => {
      const res = await fetch(`/api/owner/tutoriais/topicos/${topico.id}/videos`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error("Erro ao carregar vídeos");
      return res.json();
    },
    enabled: !!token,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0];
      if (!file) throw new Error("Selecione o arquivo de vídeo");
      const params = new URLSearchParams({ nomeArquivo: file.name });
      const res = await fetch(`/api/owner/tutoriais/topicos/${topico.id}/videos?${params}`, {
        method: "POST",
        headers: { ...authHeaders(token), "Content-Type": "video/mp4" },
        body: file,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao enviar vídeo");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/topicos", topico.id, "videos"] });
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast({ title: "Vídeo enviado com sucesso" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const setPrincipalMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/owner/tutoriais/videos/${id}/principal`, { method: "PUT", headers: authHeaders(token) });
      if (!res.ok) throw new Error("Erro ao definir como principal");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/topicos", topico.id, "videos"] });
      toast({ title: "Vídeo principal atualizado" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/owner/tutoriais/videos/${id}`, { method: "DELETE", headers: authHeaders(token) });
      if (!res.ok) throw new Error("Erro ao excluir vídeo");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/topicos", topico.id, "videos"] });
      toast({ title: "Vídeo removido" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor={`video-file-${topico.id}`} className="text-xs">Novo vídeo (.mp4)</Label>
          <Input id={`video-file-${topico.id}`} type="file" accept=".mp4" ref={fileInputRef} />
        </div>
        <Button size="sm" onClick={() => uploadMutation.mutate()} disabled={uploadMutation.isPending}>
          {uploadMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1.5" />}
          Enviar
        </Button>
      </div>
      {isLoading ? (
        <Skeleton className="h-10 rounded-lg" />
      ) : !videos || videos.length === 0 ? (
        <p className="text-xs text-slate-500">Nenhum vídeo enviado ainda para este tópico.</p>
      ) : (
        videos.map((video) => (
          <div key={video.id} className="flex items-center justify-between text-sm px-2 py-1.5 bg-slate-50 rounded">
            <div className="flex items-center gap-2">
              <span className="font-medium">v{video.versao}</span>
              {video.principal && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Principal</Badge>}
              <span className="text-xs text-slate-500">{video.nomeArquivo} · {formatBytes(video.tamanhoBytes)}</span>
            </div>
            <div className="flex items-center gap-2">
              {!video.principal && (
                <Button size="sm" variant="outline" onClick={() => setPrincipalMutation.mutate(video.id)} disabled={setPrincipalMutation.isPending}>
                  <Star className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteMutation.mutate(video.id)} disabled={deleteMutation.isPending}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TopicosManager({ papel, token }: { papel: string; token: string }) {
  const { toast } = useToast();
  const [titulo, setTitulo] = useState("");

  const { data: topicos, isLoading } = useQuery<TutorialTopico[]>({
    queryKey: ["/api/owner/tutoriais/topicos", papel],
    queryFn: async () => {
      const res = await fetch(`/api/owner/tutoriais/topicos?papel=${papel}`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error("Erro ao carregar tópicos");
      return res.json();
    },
    enabled: !!token,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!titulo.trim()) throw new Error("Informe o título do tópico");
      const res = await fetch("/api/owner/tutoriais/topicos", {
        method: "POST",
        headers: { ...authHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ papel, titulo: titulo.trim() }),
      });
      if (!res.ok) throw new Error("Erro ao criar tópico");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/topicos", papel] });
      setTitulo("");
      toast({ title: "Tópico criado" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/owner/tutoriais/topicos/${id}`, { method: "DELETE", headers: authHeaders(token) });
      if (!res.ok) throw new Error("Erro ao excluir tópico");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/tutoriais/topicos", papel] });
      toast({ title: "Tópico removido" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Video className="w-4 h-4" /> Tópicos de vídeo — {ROLE_LABELS[papel]}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor={`novo-topico-${papel}`} className="text-xs">Novo tópico</Label>
            <Input id={`novo-topico-${papel}`} placeholder="ex: Registrar Veículo" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </div>
          <Button size="sm" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Criar
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-20 rounded-lg" />
        ) : !topicos || topicos.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum tópico cadastrado para este papel.</p>
        ) : (
          <div className="space-y-3">
            {topicos.map((topico) => (
              <div key={topico.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">{topico.titulo}</p>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteMutation.mutate(topico.id)} disabled={deleteMutation.isPending}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <TopicoVideos topico={topico} token={token} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TutoriaisAdmin() {
  const { profile, session, loading } = useAuth();
  const [, navigate] = useLocation();
  const isOwner = profile?.roles?.includes("owner") ?? false;
  const token = session?.access_token ?? "";
  const [papelSelecionado, setPapelSelecionado] = useState<(typeof PAPEIS_TUTORIAL)[number]>("tv");

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (!isOwner) {
    navigate("/");
    return null;
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-slate-900">Gerenciar Tutoriais</h1>
        <p className="text-muted-foreground mt-1">
          Envie o PDF completo e os vídeos por tópico de cada papel
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        {PAPEIS_TUTORIAL.map((papel) => (
          <Button
            key={papel}
            size="sm"
            variant={papelSelecionado === papel ? "default" : "outline"}
            onClick={() => setPapelSelecionado(papel)}
          >
            {ROLE_LABELS[papel]}
          </Button>
        ))}
      </div>

      <div className="max-w-2xl space-y-4">
        <PdfManager papel={papelSelecionado} token={token} />
        <TopicosManager papel={papelSelecionado} token={token} />
      </div>
    </Shell>
  );
}
